const KEY = "research:last-thread"

/**
 * Remembers only the id of the last conversation — never its contents.
 *
 * The conversation itself lives in `research_jobs` and is fetched from GET /threads/{id},
 * which is user-scoped. Caching the answers client-side would create a second source of
 * truth that can disagree with the server and can't follow the user to another device; an
 * id is just a bookmark, and the server still decides whether this user may read it.
 *
 * Exposed as a `useSyncExternalStore` source rather than something a component copies into
 * state inside an effect: localStorage is unavailable during SSR, so it can't be read during
 * render, and syncing it in via useEffect causes a cascading re-render. `getServerSnapshot`
 * returns null so the server and first client render agree.
 */

const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((listener) => listener())
}

export function subscribeLastThread(listener: () => void): () => void {
  listeners.add(listener)
  // Keep other tabs in step: clearing or starting a conversation elsewhere should not leave
  // a stale "Resume" pointing at something the user just abandoned.
  window.addEventListener("storage", listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener("storage", listener)
  }
}

export function getLastThreadSnapshot(): string | null {
  try {
    return window.localStorage.getItem(KEY)
  } catch {
    // Private browsing / storage disabled — resuming is a convenience, never a requirement.
    return null
  }
}

/** Server render (and the hydrating client render) always sees "no resumable thread". */
export function getLastThreadServerSnapshot(): string | null {
  return null
}

export function rememberThread(threadId: string): void {
  try {
    if (window.localStorage.getItem(KEY) === threadId) return
    window.localStorage.setItem(KEY, threadId)
    emit()
  } catch {
    // ignore
  }
}

export function forgetThread(): void {
  try {
    window.localStorage.removeItem(KEY)
    emit()
  } catch {
    // ignore
  }
}
